<template>
  <div class="auth-shell">
    <!-- toast -->
    <transition name="toast">
      <div v-if="showSnackBar" class="toast" role="status">
        <i class="mdi mdi-information-outline toast-icon"></i>
        <span class="toast-text">{{ msg }}</span>
        <button type="button" class="toast-close" aria-label="Dismiss" @click="showSnackBar = false">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </transition>

    <!-- official strip -->
    <header class="gov-strip">
      <div class="gov-strip-inner">
        <img src="../assets/odisha_1.png" alt="Odisha Forest Department" class="gov-wordmark" />
      </div>
    </header>

    <main class="auth-body">
      <!-- ============== form pane ============== -->
      <section class="form-pane">
        <!-- background image -->
        <img src="../assets/images/left_bg_logn.png" alt="" class="form-bg-img" aria-hidden="true" />
        <div class="form-bg-scrim"></div>
        <!-- topographic texture -->
        <svg class="contours" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <g fill="none" stroke="#34d399" stroke-width="1">
            <path d="M-40 120 C 120 60, 260 200, 420 130 S 640 60, 700 150" />
            <path d="M-40 190 C 120 130, 260 270, 420 200 S 640 130, 700 220" />
            <path d="M-40 260 C 120 200, 260 340, 420 270 S 640 200, 700 290" />
            <path d="M-40 330 C 120 270, 260 410, 420 340 S 640 270, 700 360" />
            <path d="M-40 470 C 120 410, 260 550, 420 480 S 640 410, 700 500" />
            <path d="M-40 540 C 120 480, 260 620, 420 550 S 640 480, 700 570" />
            <path d="M-40 610 C 120 550, 260 690, 420 620 S 640 550, 700 640" />
            <path d="M-40 680 C 120 620, 260 760, 420 690 S 640 620, 700 710" />
          </g>
        </svg>

        <div class="form-inner">
          <div class="login-card">
            <!-- brand — carried by the header strip once the layout stacks -->
            <div class="brand-section">
              <div class="brand-logo">
                <img src="/sarpaico.ico" alt="SARPA" class="brand-logo-img" />
              </div>
              <h2 class="brand-title">SARPA <span class="brand-dash">–</span> <span class="brand-accent">ODISHA</span></h2>
              <div class="gov-line">
                <i class="mdi mdi-bank gov-line-icon"></i>
                <span>Government of Odisha</span>
              </div>
            </div>

            <div class="rule">
              <span class="rule-line"></span>
              <span class="rule-badge"><i class="mdi mdi-shield-check"></i></span>
              <span class="rule-line"></span>
            </div>

            <h1 class="welcome">Welcome <span class="welcome-accent">Back!</span></h1>
            <p class="welcome-sub">Sign in to access the Sarpa Odisha administration portal</p>

            <form class="fields" autocomplete="on" @submit.prevent="validateInput">
              <label class="field-label" for="auth-username">Username</label>
              <div class="field" :class="{ 'is-focused': focus === 'user' }">
                <i class="mdi mdi-account-outline field-icon"></i>
                <input
                  id="auth-username"
                  v-model="emailId"
                  type="text"
                  class="field-input"
                  placeholder="Enter your username"
                  autocomplete="username"
                  spellcheck="false"
                  @focus="focus = 'user'"
                  @blur="focus = null"
                />
              </div>

              <label class="field-label mt-18" for="auth-password">Password</label>
              <div class="field" :class="{ 'is-focused': focus === 'pass' }">
                <i class="mdi mdi-lock-outline field-icon"></i>
                <input
                  id="auth-password"
                  v-model="password"
                  :type="show3 ? 'text' : 'password'"
                  class="field-input"
                  placeholder="Enter your password"
                  autocomplete="current-password"
                  @focus="focus = 'pass'"
                  @blur="focus = null"
                />
                <button
                  type="button"
                  class="field-toggle"
                  :aria-label="show3 ? 'Hide password' : 'Show password'"
                  @click="show3 = !show3"
                >
                  <i class="mdi" :class="show3 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"></i>
                </button>
              </div>

              <div class="row-between mt-18">
                <label class="remember">
                  <input v-model="rememberMe" type="checkbox" class="remember-input" />
                  <span class="remember-box"><i class="mdi mdi-check"></i></span>
                  <span class="remember-text">Remember my username</span>
                </label>
                <router-link to="/forgotPassword" class="quiet-link">Forgot Password?</router-link>
              </div>

              <button type="submit" class="signin-btn" :disabled="appLoading">
                <span v-if="!appLoading">SIGN IN</span>
                <span v-else>SIGNING IN&hellip;</span>
                <i v-if="!appLoading" class="mdi mdi-arrow-right btn-arrow"></i>
                <span v-else class="spinner" aria-hidden="true"></span>
              </button>
            </form>

            <div class="rule subtle">
              <span class="rule-line"></span>
              <span class="rule-text">secure sign-in</span>
              <span class="rule-line"></span>
            </div>

            <div class="chips">
              <span class="chip"><i class="mdi mdi-lock-check-outline"></i> Encrypted Session</span>
              <span class="chip"><i class="mdi mdi-account-key-outline"></i> Authorised Access Only</span>
            </div>

            <p class="support-line">
              Trouble signing in?
              <router-link to="/supportPage" class="support-link">Get support</router-link>
            </p>
          </div> <!-- end .login-card -->
        </div>
      </section>

      <!-- ============== imagery pane ============== -->
      <section class="hero-pane">
        <img
          src="../assets/login_dashboard_image.png"
          alt="A pit viper resting on a mossy branch"
          class="hero-img"
        />
        <div class="hero-scrim"></div>

        <div class="hero-content">
          <!-- top section -->
          <div class="hero-top">
            <div class="hero-eyebrow">
              <i class="mdi mdi-shield-check-outline eyebrow-icon"></i>
              Statewide Snake Rescue Network
            </div>

            <h2 class="hero-title">
              Protecting<br />
              <span class="hero-accent">Wildlife,</span><br />
              Securing Lives.
            </h2>
            <p class="hero-sub">
              Snake Rescue &amp; Human&ndash;Wildlife Conflict
              Management System for Odisha
            </p>
          </div>

          <!-- feature cards -->
          <div class="feature-row">
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <i class="mdi mdi-clock-outline"></i>
              </div>
              <div class="feature-value">24/7</div>
              <div class="feature-label">Rescue Support</div>
            </div>
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <i class="mdi mdi-map-marker-radius-outline"></i>
              </div>
              <div class="feature-value">All Districts</div>
              <div class="feature-label">Network Coverage</div>
            </div>
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <i class="mdi mdi-medical-bag"></i>
              </div>
              <div class="feature-value">Safe Rescue</div>
              <div class="feature-label">First Approach</div>
            </div>
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <i class="mdi mdi-handshake-outline"></i>
              </div>
              <div class="feature-value">Coexistence</div>
              <div class="feature-label">With Nature</div>
            </div>
          </div>

          <!-- mission -->
          <div class="mission-bar">
            <div class="mission-icon-wrap">
              <img src="/sarpaico.ico" alt="SARPA" class="mission-logo" />
            </div>
            <div class="mission-text">
              <span class="mission-heading">Our Mission</span>
              <span class="mission-body">Together, we can create a safer coexistence with nature.</span>
            </div>
          </div>
        </div>
      </section>

      <!-- a sibling of both panes, not a child of the form: stacked it has to
           come after the hero, and inside the form pane it could not -->
      <footer class="pane-footer">
        <span>&copy; {{ year }} Sarpa Odisha. All rights reserved.</span>
        <span class="divider-dot">|</span>
        <span>
          Designed &amp; Maintained by
          <a href="https://leopardtechlabs.com" target="_blank" rel="noopener" class="ltl-link"
            >Leopard Tech Labs</a
          >
        </span>
      </footer>
    </main>
  </div>
</template>

<script>
import axios from "axios";

//prefill the username next visit when the officer opts in
const REMEMBERED_USERNAME = "sarpa_remembered_username";

export default {
  data() {
    return {
      ServerError: false,
      showSnackBar: false,
      msg: null,
      emailId: null,
      password: null,
      show3: false,
      rememberMe: false,
      appLoading: false,
      focus: null,
      year: new Date().getFullYear(),
      toastTimer: null,
    };
  },
  mounted() {
    const saved = localStorage.getItem(REMEMBERED_USERNAME);
    if (saved) {
      this.emailId = saved;
      this.rememberMe = true;
    }
  },
  beforeDestroy() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    notify(message) {
      this.msg = message;
      this.showSnackBar = true;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.showSnackBar = false;
      }, 4000);
    },

    validateInput() {
      if (this.appLoading) return;
      if (!this.emailId) {
        this.notify("Please provide your username.");
        return;
      }
      if (!this.password) {
        this.notify("Please provide your password.");
        return;
      }
      this.login();
    },

    login() {
      this.appLoading = true;
      axios({
        method: "post",
        url: "/platform/login/",
        data: {
          username: this.emailId,
          password: this.password,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.heartbeat) {
            this.notify(response.data.msg);

            if (this.rememberMe) {
              localStorage.setItem(REMEMBERED_USERNAME, this.emailId);
            } else {
              localStorage.removeItem(REMEMBERED_USERNAME);
            }

            //OLD WILDWATCH REQ
            localStorage.setItem("token", response.data.data.token);
            localStorage.setItem("userRole", response.data.data.type);
            if (response.data.data.district) {
              localStorage.setItem("district", response.data.data.district);
            }
            this.$store.commit("userData", response.data.userDetails);
            this.$store.commit("loginUser", response.data.data.token);
            this.$store.commit("userRole", response.data.data.type);
            this.$store.commit("mainRole", response.data.data.role);
            this.$store.commit("userName", response.data.data.username);

            if (response.data.data.role == "official")
              this.$router.push("/officers/sarpadashboard");
            else if (response.data.data.type == "admin")
              this.$router.push("/Sarpa/dashboard");
            else if (response.data.data.type == "RRT")
              this.$router.push("/officers/sarpadashboard");
            else if (response.data.data.role == "District Medical Officer" || response.data.data.role == "DistrictMedicalOfficer" || response.data.data.role == "DMO" || response.data.data.type == "District Medical Officer" || response.data.data.type == "DistrictMedicalOfficer" || response.data.data.type == "DMO")
              this.$router.push("/districtMedical");
            else if (response.data.data.role == "MedicalOfficer" || response.data.data.type == "MedicalOfficer")
              this.$router.push("/medicalOfficers/sarpadashboard");
            else if (response.data.data.role == "District Emergency Officer")
              this.$router.push("/DistrictEmergencyOfficer/districtDashboard");
            else if (response.data.data.role == "StateMedicalOfficer" || response.data.data.type == "StateMedicalOfficer")
              this.$router.push("/stateMedical");
            else if (response.data.data.role == "Disaster Management Officer" || response.data.data.type == "Disaster Management Officer" || response.data.data.role == "DisasterManagementOfficer")
              this.$router.push("/disaster");
            else if (response.data.data.role == "StateDisasterOfficer" || response.data.data.type == "StateDisasterOfficer" || response.data.data.role == "State Disaster Officer" || response.data.data.role == "State Disaster Management Officer")
              this.$router.push("/stateDisaster");
          } else {
            this.notify(response.data.msg);
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          this.notify("Unable to reach the server. Please try again.");
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
/* ============================================================================
 * LAYOUT MODEL
 *
 * Two layouts, not eight. Everything below is written stacked-first — hero band
 * on top, form beneath, page scrolls — because that is what phones and portrait
 * tablets get. One media query near the bottom switches to the side-by-side
 * split, which locks to the viewport and never scrolls.
 *
 * Split applies from 1024px, and from 768px when the device is landscape, so a
 * portrait tablet at 834px stacks rather than squeezing a tall form beside a
 * tall photo. Both arms require 480px of height, which keeps landscape phones
 * on the stacked, scrollable layout where they belong.
 *
 * Sizing runs off a single fluid unit `--u`; the reference design was drawn at
 * --u: 14px, so a value of 24px reads as 1.7u.
 * ========================================================================== */
.auth-shell {
  --u: clamp(11px, 3.2vw, 15px);
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #0b1f12;
  font-family: "poppinsregular", sans-serif;
  -webkit-font-smoothing: antialiased;
}
.mt-18 { margin-top: calc(var(--u) * 1.28); }

/* ====================== TOAST ====================== */
.toast {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 60;
  display: flex;
  align-items: center;
  max-width: 380px;
  padding: 12px 14px;
  border: 1px solid rgba(52, 211, 153, 0.28);
  border-radius: 10px;
  background: #14301e;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.42);
}
.toast-icon { color: #34d399; font-size: 18px; margin-right: 10px; flex: 0 0 auto; }
.toast-text { color: #fff; font-size: 13px; line-height: 1.4; flex: 1; }
.toast-close {
  flex: 0 0 auto; margin-left: 12px; padding: 2px; background: none;
  border: none; cursor: pointer; color: rgba(255,255,255,.72); font-size: 16px; line-height: 1;
}
.toast-close:hover { color: #fff; }
.toast-enter-active, .toast-leave-active { transition: opacity .28s ease, transform .28s ease; }
.toast-enter, .toast-leave-to { opacity: 0; transform: translateY(-10px); }

/* ====================== OFFICIAL STRIP ====================== */
.gov-strip {
  position: relative;
  z-index: 40;
  flex: 0 0 auto;
  background: #ecfdf5;
  border-bottom: 1px solid rgba(5, 150, 105, 0.1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.gov-strip-inner {
  max-width: 1560px; margin: 0 auto;
  padding: calc(var(--u) * 0.57) calc(var(--u) * 1.86);
  display: flex; align-items: center; gap: calc(var(--u) * 1.14);
}
.gov-wordmark {
  height: clamp(20px, calc(var(--u) * 2), 30px);
  width: auto; max-width: 100%; object-fit: contain; flex: 0 1 auto;
}

/* hamburger — the support and policy links have nowhere else to go once the
   layout stacks, so they move in here rather than being dropped */

/* ====================== BODY ====================== */
.auth-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

/* ====================== FORM PANE ====================== */
/*
 * Stacked: a plain mint section, first on the page. Signing in is what someone
 * opened this on a phone to do, so the form comes before the hero band rather
 * than after a screenful of it. Order also matches DOM order here.
 */
.form-pane {
  order: 1;
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(170deg, #f0fdf4 0%, #ecfdf5 55%, #dcfce7 100%);
}
/* the photo backdrop, its scrim and the contour texture belong to the split
   layout only — stacked, the hero band above already carries the imagery */
.form-bg-img,
.form-bg-scrim,
.contours { display: none; }
.form-inner {
  position: relative; z-index: 1;
  flex: 1 1 auto; width: 100%; max-width: 480px; min-height: 0;
  margin: 0 auto;
  padding: calc(var(--u) * 1.7) calc(var(--u) * 1.45) calc(var(--u) * 0.85);
  display: flex; flex-direction: column; justify-content: center;
}

/* ====================== LOGIN CARD ====================== */
/* stacked: a real card on the mint section, not a flat run of fields */
.login-card {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(5, 150, 105, 0.14);
  border-radius: calc(var(--u) * 1.5);
  padding: calc(var(--u) * 2) calc(var(--u) * 1.7) calc(var(--u) * 1.8);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 18px 44px -22px rgba(6, 24, 14, 0.35);
}

/* ====================== BRAND SECTION ====================== */
/* the header strip carries the department, not Sarpa, so the mark belongs here
   on every layout - stacked included, where it used to be dropped entirely */
.brand-section { text-align: center; margin-bottom: calc(var(--u) * 0.28); }
.brand-logo {
  width: calc(var(--u) * 4.6); height: calc(var(--u) * 4.6);
  margin: 0 auto calc(var(--u) * 0.85) auto;
  border-radius: calc(var(--u) * 1.14);
  background: linear-gradient(135deg, #0ea97a 0%, #047857 100%);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 16px rgba(52, 211, 153, 0.25);
  overflow: hidden;
}
.brand-logo-img {
  width: calc(var(--u) * 3.4); height: calc(var(--u) * 3.4);
  max-width: 100%;
  object-fit: contain;
  filter: brightness(1.1);
}
.brand-title {
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 1.86);
  color: #1a2e23;
  letter-spacing: calc(var(--u) * 0.21);
  margin: 0;
}
.brand-dash { color: #9ca3af; margin: 0 calc(var(--u) * 0.28); }
.brand-accent { color: #047857; }
.gov-line {
  margin-top: calc(var(--u) * 0.43);
  display: flex; align-items: center; justify-content: center;
  gap: calc(var(--u) * 0.5);
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 0.79);
  letter-spacing: 1.1px; text-transform: uppercase;
  color: #374151;
}
.gov-line-icon { color: #047857; font-size: calc(var(--u) * 1); }

/* ====================== DIVIDERS ====================== */
.rule {
  display: flex; align-items: center; gap: calc(var(--u) * 0.85);
  margin-top: calc(var(--u) * 1.14);
}
/* the shield divider separates the brand block, which is now always present */
.rule:not(.subtle) { display: flex; }
.rule.subtle { margin-top: calc(var(--u) * 1.43); }
.rule-line {
  flex: 1; height: 1px;
  background: linear-gradient(90deg, rgba(5,150,105,0) 0%, rgba(5,150,105,.25) 50%, rgba(5,150,105,0) 100%);
}
.rule-badge {
  flex: 0 0 auto;
  width: calc(var(--u) * 1.7); height: calc(var(--u) * 1.7);
  border-radius: 50%;
  background: #047857;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 0 4px rgba(5,150,105,.12);
}
.rule-badge .mdi { color: #fff; font-size: calc(var(--u) * 0.93); }
.rule.subtle .rule-line { background: rgba(5, 150, 105, 0.12); }
.rule-text {
  flex: 0 0 auto; font-size: calc(var(--u) * 0.75);
  letter-spacing: 1.4px; text-transform: uppercase;
  color: #6b7280;
}

/* ====================== WELCOME ====================== */
.welcome {
  margin: calc(var(--u) * 1) 0 0 0;
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 1.86); font-weight: 600;
  color: #1a2e23; letter-spacing: -0.5px;
  text-align: center; line-height: 1.2;
}
.welcome-accent { color: #047857; }
.welcome-sub {
  margin: calc(var(--u) * 0.28) 0 0 0;
  font-size: calc(var(--u) * 0.86);
  color: #6b7280; text-align: center; line-height: 1.45;
}

/* ====================== FORM FIELDS ====================== */
.fields { margin-top: calc(var(--u) * 1.28); }
.field-label {
  display: block; font-size: calc(var(--u) * 0.79);
  letter-spacing: .7px; text-transform: uppercase;
  color: #374151;
  margin-bottom: calc(var(--u) * 0.43);
  font-family: "poppinsmedium", sans-serif;
}
.field {
  display: flex; align-items: center;
  /* thumb-friendly on the stacked layouts, where --u is small */
  height: calc(var(--u) * 3.7); padding: 0 calc(var(--u) * 1);
  border: 1px solid rgba(5, 150, 105, 0.18);
  border-radius: calc(var(--u) * 0.71);
  background: rgba(255, 255, 255, 0.7);
  transition: border-color .25s, background .25s, box-shadow .25s;
}
.field:hover {
  border-color: rgba(5, 150, 105, 0.3);
  background: rgba(255, 255, 255, 0.85);
}
.field.is-focused {
  border-color: #047857;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(5,150,105,.12);
}
.field-icon {
  flex: 0 0 auto; font-size: calc(var(--u) * 1.28);
  color: #9ca3af; margin-right: calc(var(--u) * 0.71);
  transition: color .25s;
}
.field.is-focused .field-icon { color: #047857; }
.field-input {
  flex: 1 1 auto; min-width: 0; width: 100%; height: 100%;
  border: none; outline: none; background: transparent;
  color: #1f2937; font-family: "poppinsregular", sans-serif;
  font-size: calc(var(--u) * 0.96);
}
.field-input::placeholder { color: #9ca3af; opacity: 1; }
.field-input:-webkit-autofill,
.field-input:-webkit-autofill:hover,
.field-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #1f2937;
  -webkit-box-shadow: 0 0 0 1000px #ecfdf5 inset;
  transition: background-color 9999s ease-in-out 0s;
}
.field-toggle {
  flex: 0 0 auto; margin-left: calc(var(--u) * 0.57); padding: calc(var(--u) * 0.28);
  background: none; border: none; cursor: pointer;
  color: #9ca3af; font-size: calc(var(--u) * 1.28); line-height: 1;
  transition: color .2s;
}
.field-toggle:hover { color: #374151; }

/* checkbox row */
.row-between {
  display: flex; align-items: center;
  justify-content: space-between; gap: calc(var(--u) * 0.71); flex-wrap: wrap;
}
.remember {
  display: inline-flex; align-items: center; cursor: pointer; user-select: none;
}
.remember-input { position: absolute; opacity: 0; width: 0; height: 0; }
.remember-box {
  width: calc(var(--u) * 1.14); height: calc(var(--u) * 1.14); flex: 0 0 auto;
  border: 1.5px solid rgba(5, 150, 105, 0.25); border-radius: 4px;
  margin-right: calc(var(--u) * 0.57);
  display: flex; align-items: center; justify-content: center;
  transition: background .2s, border-color .2s;
}
.remember-box .mdi {
  font-size: calc(var(--u) * 0.86); color: #fff; opacity: 0; transition: opacity .15s;
}
.remember-input:checked + .remember-box { background: #047857; border-color: #047857; }
.remember-input:checked + .remember-box .mdi { opacity: 1; }
.remember-input:focus-visible + .remember-box { box-shadow: 0 0 0 3px rgba(5,150,105,.2); }
.remember-text { font-size: calc(var(--u) * 0.86); color: #374151; }
.quiet-link {
  font-size: calc(var(--u) * 0.86); color: #047857 !important;
  text-decoration: none; transition: color .2s; white-space: nowrap;
}
.quiet-link:hover { color: #059669 !important; text-decoration: underline; }

/* ====================== SUBMIT BUTTON ====================== */
.signin-btn {
  width: 100%; height: calc(var(--u) * 3.9); margin-top: calc(var(--u) * 1.43);
  display: flex; align-items: center; justify-content: center; gap: calc(var(--u) * 0.64);
  border: none; border-radius: calc(var(--u) * 0.71); cursor: pointer;
  background: linear-gradient(135deg, #059669 0%, #047857 60%, #065f46 100%);
  background-size: 200% 200%;
  animation: btn-shimmer 4s ease infinite;
  color: #ffffff;
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 0.93); letter-spacing: 1.2px;
  box-shadow: 0 6px 20px rgba(5,150,105,.3);
  transition: transform .25s, box-shadow .25s, filter .25s;
}
@keyframes btn-shimmer {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.signin-btn:hover:not(:disabled) {
  transform: translateY(-2px); filter: brightness(1.1);
  box-shadow: 0 10px 30px rgba(5,150,105,.4);
}
.signin-btn:active:not(:disabled) { transform: translateY(0); }
.signin-btn:disabled { cursor: default; filter: saturate(.55) brightness(.8); box-shadow: none; animation: none; }
.btn-arrow { font-size: calc(var(--u) * 1.21); transition: transform .2s; }
.signin-btn:hover:not(:disabled) .btn-arrow { transform: translateX(4px); }
.spinner {
  width: calc(var(--u) * 1.07); height: calc(var(--u) * 1.07);
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ====================== CHIPS ====================== */
.chips {
  display: flex; flex-wrap: wrap; justify-content: center;
  gap: calc(var(--u) * 0.57); margin-top: calc(var(--u) * 1);
}
.chip {
  display: inline-flex; align-items: center; justify-content: center;
  gap: calc(var(--u) * 0.36);
  padding: calc(var(--u) * 0.43) calc(var(--u) * 1);
  border: 1px solid rgba(5, 150, 105, 0.15); border-radius: 18px;
  background: rgba(255, 255, 255, 0.6);
  font-size: calc(var(--u) * 0.79); color: #374151; white-space: nowrap;
}
.chip .mdi { color: #047857; font-size: calc(var(--u) * 0.93); }
.support-line {
  margin: calc(var(--u) * 1) 0 0 0; text-align: center;
  font-size: calc(var(--u) * 0.79); color: #6b7280;
}
.support-link {
  color: #047857 !important; text-decoration: none;
  font-family: "poppinsmedium", sans-serif;
}
.support-link:hover { text-decoration: underline; }

/* ====================== FOOTER ====================== */
/* stacked it closes the mint form section, so it wears ink rather than white */
.pane-footer {
  order: 3;
  position: relative; z-index: 1;
  flex: 0 0 auto;
  padding: calc(var(--u) * 1.14) calc(var(--u) * 1.7) calc(var(--u) * 1.28);
  text-align: center; font-size: calc(var(--u) * 0.75);
  color: #6b7280; line-height: 1.7;
  background: #dcfce7;
  border-top: 1px solid rgba(5, 150, 105, 0.12);
}
.divider-dot { margin: 0 calc(var(--u) * 0.5); color: rgba(5, 150, 105, 0.35); }
.ltl-link {
  color: #047857 !important; text-decoration: none;
  font-family: "poppinsmedium", sans-serif;
}
.ltl-link:hover { text-decoration: underline; }

/* ====================== HERO PANE ====================== */
/*
 * Stacked: a band above the form rather than a full-height column, and a flat
 * dark green one. The photo is a ~2MB PNG that, cropped into a short band on a
 * phone, showed a slice of coils rather than a snake - it cost the data and
 * gave nothing back, so it is dropped here and restored for the split layout.
 */
/*
 * Stacked, the whole right-hand section is off. On a phone the badge, headline,
 * feature tiles and mission bar are a screenful of marketing between the header
 * and the only thing anyone came here to do. The split layout has room for it
 * beside the form and switches it back on.
 */
.hero-pane {
  display: none;
  order: 2;
  position: relative;
  flex: 0 0 auto;
  overflow: hidden;
  min-width: 0;
  flex-direction: column;
  background: linear-gradient(160deg, #0d2a1a 0%, #071a10 55%, #04120a 100%);
}
@keyframes hero-zoom {
  0% { transform: scale(1.03); }
  100% { transform: scale(1.08); }
}
/*
 * 3:2 source, snake's head just right of centre. `cover` plus a subject-anchored
 * `object-position` keeps the head in frame as the pane's aspect ratio changes.
 * Both the photo and the scrim that holds it down are off until the split
 * layout switches them back on.
 */
.hero-img {
  display: none;
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: 62% center;
  transform: scale(1.03);
  animation: hero-zoom 25s ease-in-out infinite alternate;
}
.hero-scrim {
  display: none;
  position: absolute; inset: 0;
  background:
    linear-gradient(105deg,
      rgba(7,21,16,.88) 0%,
      rgba(7,21,16,.55) 35%,
      rgba(7,21,16,.12) 60%,
      rgba(7,21,16,.05) 80%
    ),
    linear-gradient(to top,
      rgba(6,18,11,.95) 0%,
      rgba(6,18,11,.65) 18%,
      rgba(6,18,11,.2) 42%,
      rgba(6,18,11,0) 65%
    );
}

/* hero content layout */
.hero-content {
  position: relative; z-index: 1;
  flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  justify-content: space-between;
  padding: calc(var(--u) * 1.7) calc(var(--u) * 1.45) calc(var(--u) * 1.7);
  color: #fff;
}
.hero-top {
  flex: 0 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  justify-content: flex-start;
}

/* eyebrow badge */
.hero-eyebrow {
  display: inline-flex; align-items: center;
  align-self: flex-start;
  padding: calc(var(--u) * 0.43) calc(var(--u) * 1.14) calc(var(--u) * 0.43) calc(var(--u) * 0.85);
  border: 1px solid rgba(52,211,153,.3);
  border-radius: 20px;
  background: rgba(6,18,11,.5);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  font-size: calc(var(--u) * 0.7); letter-spacing: .5px;
  color: #fff; text-transform: uppercase;
}
.eyebrow-icon {
  color: #34d399; font-size: calc(var(--u) * 1.14); margin-right: calc(var(--u) * 0.57);
}

/* hero title */
.hero-title {
  margin: calc(var(--u) * 1.14) 0 0 0;
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 2.4); font-weight: 600;
  line-height: 1.12; letter-spacing: -1px;
}
.hero-accent { color: #34d399; }
.hero-sub {
  margin: calc(var(--u) * 0.71) 0 0 0;
  max-width: 34ch; font-size: calc(var(--u) * 0.89);
  color: rgba(255,255,255,.9); line-height: 1.55;
}

/* ====================== FEATURE CARDS ====================== */
/* stacked default is the 2x2 grid from screens 5 and 6 */
.feature-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(var(--u) * 0.71);
  margin-top: calc(var(--u) * 1.28);
}
.feature-card {
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  min-width: 0;
  padding: calc(var(--u) * 1.14) calc(var(--u) * 0.71) calc(var(--u) * 1);
  border: 1px solid rgba(52, 211, 153, 0.2);
  border-radius: calc(var(--u) * 1.14);
  background: rgba(10, 30, 20, 0.65);
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(52, 211, 153, 0.08);
  transition: transform .25s, box-shadow .25s, border-color .25s;
}
.feature-card:hover {
  transform: translateY(-3px);
  border-color: rgba(52, 211, 153, 0.4);
  box-shadow:
    0 8px 30px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(52, 211, 153, 0.08),
    inset 0 1px 0 rgba(52, 211, 153, 0.12);
}
.feature-icon-wrap {
  width: calc(var(--u) * 2.7); height: calc(var(--u) * 2.7);
  flex: 0 0 auto;
  border-radius: 50%;
  border: 1.5px solid rgba(52, 211, 153, 0.35);
  background: rgba(52, 211, 153, 0.08);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: calc(var(--u) * 0.57);
}
.feature-icon-wrap .mdi {
  font-size: calc(var(--u) * 1.35); color: #34d399;
}
.feature-value {
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 0.93); color: #ffffff;
  letter-spacing: .3px; line-height: 1.3;
}
.feature-label {
  font-size: calc(var(--u) * 0.75); color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

/* ====================== MISSION BAR ====================== */
.mission-bar {
  display: flex; align-items: center; gap: calc(var(--u) * 1);
  margin-top: calc(var(--u) * 0.86);
  padding: calc(var(--u) * 1) calc(var(--u) * 1.14);
  border: 1px solid rgba(52, 211, 153, 0.2);
  border-radius: calc(var(--u) * 1.14);
  background: rgba(10, 30, 20, 0.65);
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(52, 211, 153, 0.08);
}
.mission-icon-wrap {
  width: calc(var(--u) * 3); height: calc(var(--u) * 3); flex: 0 0 auto;
  border-radius: calc(var(--u) * 0.86);
  background: linear-gradient(135deg, #0ea97a 0%, #047857 100%);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(52, 211, 153, 0.2);
}
.mission-logo {
  width: calc(var(--u) * 2.2); height: calc(var(--u) * 2.2);
  max-width: 100%; object-fit: contain;
}
.mission-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.mission-heading {
  font-family: "poppinssemibold", sans-serif;
  font-size: calc(var(--u) * 0.93); color: #34d399;
  letter-spacing: .3px;
}
.mission-body {
  font-size: calc(var(--u) * 0.82); color: rgba(255, 255, 255, 0.8);
  line-height: 1.45;
}

/* ============================================================================
 * SCREENS 7 & 8 — mobile medium (375–429) and small (320–374)
 * Four tiles side by side stop being tiles and become a list; the icon moves
 * beside the words instead of above them.
 * ========================================================================== */
@media (max-width: 429px) {
  .feature-row { grid-template-columns: minmax(0, 1fr); gap: calc(var(--u) * 0.5); }
  .feature-card {
    flex-direction: row; align-items: center; text-align: left;
    gap: calc(var(--u) * 0.71);
    padding: calc(var(--u) * 0.71) calc(var(--u) * 0.93);
    border-radius: calc(var(--u) * 0.86);
  }
  .feature-icon-wrap {
    width: calc(var(--u) * 2.1); height: calc(var(--u) * 2.1);
    margin-bottom: 0;
  }
  .feature-icon-wrap .mdi { font-size: calc(var(--u) * 1.14); }
  .feature-label { margin-top: 0; }
  .hero-title { font-size: calc(var(--u) * 2.15); }
  /* the band is narrow enough here that 62% leaves the head on the very edge */
  .hero-img { object-position: 68% 45%; }
}

/* screen 8 only: two chips will not sit on one 320px line legibly */
@media (max-width: 374px) {
  .auth-shell { --u: clamp(10.5px, 3.5vw, 13px); }
  .chips { flex-direction: column; align-items: stretch; }
  .chip { width: 100%; }
  .gov-strip-inner { padding: calc(var(--u) * 0.57) calc(var(--u) * 1.14); }
  .pane-footer .divider-dot { display: none; }
  .pane-footer span { display: block; }
}

/* ============================================================================
 * SCREENS 1–4 — the side-by-side split
 *
 * From 1024px, or from 768px in landscape. Both arms need 480px of height, so
 * a landscape phone keeps the stacked layout instead of cramming the split into
 * 390px. This is the one place the page locks to the viewport and stops
 * scrolling; everything above it is a scrolling document.
 * ========================================================================== */
@media (min-width: 1024px) and (min-height: 480px),
       (min-width: 768px) and (min-height: 480px) and (orientation: landscape) {
  .auth-shell {
    --u: clamp(10px, min(1.05vw, 1.45vh), 14px);
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  /* position: the pinned footer below anchors to this */
  .auth-body { flex-direction: row; position: relative; }

  /* ---- form pane: left column, photo backdrop, floating card ---- */
  .form-pane {
    order: 0;
    flex: 0 0 42%;
    max-width: 42%;
    background: #06120c;
    overflow: hidden;
  }
  /*
   * The source art is 16:9 with the snake sitting at roughly 15% from the left,
   * so a centred crop in this narrow, tall pane shows only empty canopy. Anchor
   * the crop to the subject instead.
   */
  .form-bg-img {
    display: block;
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover;
    object-position: 22% center;
    opacity: 0.35;
    pointer-events: none;
  }
  .form-bg-scrim {
    display: block;
    position: absolute; inset: 0;
    background:
      linear-gradient(180deg,
        rgba(6,18,12,0.7) 0%,
        rgba(6,18,12,0.5) 40%,
        rgba(6,18,12,0.65) 80%,
        rgba(6,18,12,0.85) 100%
      );
    pointer-events: none;
  }
  .contours {
    display: block;
    position: absolute; inset: 0; width: 100%; height: 100%;
    opacity: 0.06; pointer-events: none;
  }
  .form-inner { max-width: 460px; }

  .login-card {
    background: linear-gradient(155deg, #f0fdf4 0%, #dcfce7 50%, #ecfdf5 100%);
    border: 1px solid rgba(5, 150, 105, 0.1);
    border-radius: calc(var(--u) * 1.6);
    padding: calc(var(--u) * 2.3) calc(var(--u) * 2.45) calc(var(--u) * 1.9);
    box-shadow:
      0 20px 60px rgba(0, 0, 0, 0.25),
      0 4px 16px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  .field { height: calc(var(--u) * 3.3); background: rgba(255, 255, 255, 0.6); }
  .field.is-focused { background: rgba(255, 255, 255, 0.85); }
  .signin-btn { height: calc(var(--u) * 3.43); }
  .chip { padding: calc(var(--u) * 0.36) calc(var(--u) * 1); }

  /*
   * The footer is a sibling of the panes rather than a child of the form, so
   * in a two-column row it would become a third column. Lift it out of the
   * flow and pin it across the bottom of the left column, where it sits on the
   * form pane's own photo backdrop exactly as it did before.
   */
  .pane-footer {
    position: absolute;
    left: 0; bottom: 0;
    width: 42%;
    padding: calc(var(--u) * 0.71) calc(var(--u) * 1.7) calc(var(--u) * 1);
    background: transparent;
    border-top: none;
    color: rgba(255,255,255,.75);
  }
  .divider-dot { color: rgba(255,255,255,.4); }
  .ltl-link { color: #34d399 !important; }
  /* the card is centred in the pane, so give the pinned footer its space back */
  .form-inner { padding-bottom: calc(var(--u) * 3.5); }

  /* ---- hero pane: right column, full height ---- */
  /* there is room for the photo here, so bring it and its scrim back */
  .hero-pane { display: flex; order: 0; flex: 1 1 auto; background: #06120c; }
  .hero-img { display: block; }
  .hero-scrim { display: block; }
  .hero-content {
    padding: calc(var(--u) * 2.57) calc(var(--u) * 3.14) calc(var(--u) * 2);
  }
  .hero-top { flex: 1 1 auto; }
  .hero-eyebrow { font-size: calc(var(--u) * 0.79); }
  .hero-title { margin-top: calc(var(--u) * 1.7); font-size: calc(var(--u) * 3.14); letter-spacing: -1.2px; }
  .hero-sub { margin-top: calc(var(--u) * 1); font-size: calc(var(--u) * 0.96); }

  /* screen 4 keeps the 2x2 grid; four across needs the room screen 3 has */
  .feature-row { gap: calc(var(--u) * 1); margin-top: calc(var(--u) * 1.7); }
  .feature-card { padding: calc(var(--u) * 1.43) calc(var(--u) * 0.71) calc(var(--u) * 1.14); }
  .feature-icon-wrap {
    width: calc(var(--u) * 3.3); height: calc(var(--u) * 3.3);
    margin-bottom: calc(var(--u) * 0.71);
  }
  .feature-icon-wrap .mdi { font-size: calc(var(--u) * 1.57); }
  .feature-value { font-size: calc(var(--u) * 1); }
  .feature-label { font-size: calc(var(--u) * 0.79); }

  .mission-bar {
    gap: calc(var(--u) * 1.14);
    margin-top: calc(var(--u) * 1.14);
    padding: calc(var(--u) * 1.28) calc(var(--u) * 1.57);
  }
  .mission-icon-wrap {
    width: calc(var(--u) * 3.7); height: calc(var(--u) * 3.7);
    border-radius: calc(var(--u) * 1);
  }
  .mission-logo { width: calc(var(--u) * 2.7); height: calc(var(--u) * 2.7); }
  .mission-heading { font-size: calc(var(--u) * 1); }
  .mission-body { font-size: calc(var(--u) * 0.89); }
}

/* screens 1–3: enough width for the four tiles to sit in one row */
@media (min-width: 1024px) and (min-height: 480px) {
  .feature-row { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

/* screen 1: cap the form column so the card never floats in a huge empty gutter */
@media (min-width: 1700px) and (min-height: 480px) {
  .form-pane { flex: 0 0 620px; max-width: 620px; }
  /* the pinned footer has to track the column it sits under */
  .pane-footer { width: 620px; }
  .form-bg-img { object-position: 18% center; }
}

/*
 * Split layout on a short screen — a 1024x600 netbook, a 1366x640 kiosk. The
 * reassurance block is what gets dropped so the fields and the CTA stay in view
 * without a scrollbar.
 */
@media (min-width: 768px) and (max-height: 700px) and (orientation: landscape) {
  .rule.subtle, .chips { display: none; }
}
@media (min-width: 768px) and (max-height: 620px) and (orientation: landscape) {
  .rule:not(.subtle), .support-line { display: none; }
  .brand-section { margin-bottom: 0; }
}

/* ============================================================================
 * SUBJECT-SAFE CROPPING
 *
 * Both photos are landscape — the hero is 1536x1024 (3:2), the backdrop
 * 1617x972 — and both put their subject well off-centre. The viper's head sits
 * at roughly 75% across and 20% down; the backdrop's snake sits at about 19%
 * across. In the split layout the panes are tall columns, so `cover` scales the
 * image by HEIGHT and discards the sides: the taller the viewport, the narrower
 * the surviving slice.
 *
 * At the default 62% anchor that slice is 44%–73% of the hero on a 1024x1400
 * window — the head, at 75%, lands just outside it, which is the head that goes
 * missing. Walking the anchor right as the viewport gets taller keeps the
 * subject inside the slice at every shape: 55%–84% at that same size.
 *
 * These are scoped to the split layout deliberately. Stacked, the hero is a
 * wide band rather than a column and the base anchor already frames it; the
 * narrow-phone case is handled in the max-width: 429px block above.
 * ========================================================================== */

/* viewport narrower than 3:2 — includes every laptop that is not widescreen,
   and the 768–1023 landscape tablets */
@media (min-width: 1024px) and (min-height: 480px) and (max-aspect-ratio: 3/2),
       (min-width: 768px) and (min-height: 480px) and (orientation: landscape) and (max-aspect-ratio: 3/2) {
  .hero-img { object-position: 68% 46%; }
  .form-bg-img { object-position: 20% center; }
}

/* square or taller — tall monitors, half-screen windows, rotated displays */
@media (min-width: 1024px) and (min-height: 480px) and (max-aspect-ratio: 1/1) {
  .hero-img { object-position: 78% 44%; }
  .form-bg-img { object-position: 18% 45%; }
  /*
   * `space-between` over a 1300px column parks the title at the ceiling and the
   * tiles on the floor with a void between them. Past square, cluster the block
   * and centre it instead.
   */
  .hero-content { justify-content: center; gap: calc(var(--u) * 2.2); }
  .hero-top { flex: 0 1 auto; }
  /*
   * The slow zoom crops a further 8% from the centre. With the head already
   * near the edge of a narrow slice that is enough to clip it, so hold still.
   */
  .hero-img { animation: none; transform: none; }
}

/* distinctly portrait — the slice is at its narrowest here */
@media (min-width: 1024px) and (min-height: 480px) and (max-aspect-ratio: 3/4) {
  .hero-img { object-position: 82% 42%; }
  .form-bg-img { object-position: 16% 45%; }
}

@media (prefers-reduced-motion: reduce) {
  .hero-img { animation: none; transform: none; }
  .signin-btn { animation: none; }
  .feature-card, .signin-btn, .field, .btn-arrow,
  .toast-enter-active, .toast-leave-active {
    transition: none !important;
  }
  .feature-card:hover, .signin-btn:hover:not(:disabled) { transform: none; }
}
</style>
